/**
 * ProcessMatchingPage — Matching screen vertical (§9-11 spec + M1b).
 *
 * Block M1a-FE (TZ 2026-07-29) + M1b (TZ 2026-07-29):
 *   - ProcessControlBar kind='matching' (7 slots, ≥1280px one row)
 *   - Composition block (§9.2): 0..1 base template + 0..N manual properties
 *   - Input column chips (evidence source, denylist)
 *   - Aggregate purple chips + RemoveConfirmModal
 *   - Disputed horizontal carousel 4-5 cards + eye modal
 *   - UnresolvedSummary counter
 *   - Confirm categories primary button + token display
 *   - DeterminedPropertiesGrid + delete modal + multivalue toggle (§13)
 *   - ResultsTable minimal unified grid (§14.1)
 *   - Manual-only path: Start goes straight to property stage (no categories)
 *
 * DEFERRED (per TZ):
 *   - Layer export, pin-3 grid, excel-filter (M2+)
 *   - LLM-aided disputed (M1c)
 *   - L2 library project_id
 *
 * Wire contract:
 *   - BE endpoints не трогаем.
 *   - hooks/useMatching.ts неизменён shape (новые хуки добавлены для M1b).
 *   - matchingStore — persistent UI state (added property overrides).
 *   - Все hooks module-level (NO dynamic import).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Database,
  Download,
  Info,
  Plus,
  RefreshCw,
  Sparkles,
} from 'lucide-react';

import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { ProcessControlBar, type ProcessExecutionState } from '@/components/process/ProcessControlBar';
import { MethodBlock } from '@/components/matching/MethodBlock';
import { InputColumnChips } from '@/components/matching/InputColumnChips';
import { AggregateChips, type AggregateChipData } from '@/components/matching/AggregateChips';
import { RemoveConfirmModal } from '@/components/matching/RemoveConfirmModal';
import { DisputedCarousel } from '@/components/matching/DisputedCarousel';
import { DisputedEyeModal } from '@/components/matching/DisputedEyeModal';
import type { DisputedCardData } from '@/components/matching/DisputedCard';
import { ConfirmCategoriesButton } from '@/components/matching/ConfirmCategoriesButton';
import { DeterminedPropertiesGrid } from '@/components/matching/DeterminedPropertiesGrid';
import { ResultsTable } from '@/components/matching/ResultsTable';
import { UnresolvedSummary } from '@/components/matching/UnresolvedSummary';
import { DiscoveredPropertiesQueue } from '@/components/matching/DiscoveredPropertiesQueue';
import { AddCategoryModal } from '@/components/matching/AddCategoryModal';

import { useProjectStore } from '@/store/projectStore';
import { useMatchingStore } from '@/store/matchingStore';
import { useDownloadBlankTemplate } from '@/hooks/useAdmin';
import {
  useStartMatching,
  usePipelineMatchingRows,
  useStartProperties,
} from '@/hooks/useMatching';
import type { PipelineRun } from '@/hooks/useGeneration';
import { usePipelineStatus } from '@/hooks/useGeneration';
import { cn } from '@/lib/utils';
import { extractApiError } from '@/lib/apiError';

export function ProcessMatchingPage() {
  const { t } = useTranslation();
  const projectId = useProjectStore((s) => s.currentProjectId);

  // === matchingStore (persistent UI state) ===
  const templateId = useMatchingStore((s) => s.templateId);
  const language = useMatchingStore((s) => s.language);
  const manualProperties = useMatchingStore((s) => s.manualProperties);
  const selectedInputColumns = useMatchingStore((s) => s.selectedInputColumns);
  const multivalueDelimiter = useMatchingStore((s) => s.multivalueDelimiter);
  const tierKey = useMatchingStore((s) => s.tierKey);
  const setTierKey = useMatchingStore((s) => s.setTierKey);
  const isTestRun = useMatchingStore((s) => s.isTestRun);
  const setIsTestRun = useMatchingStore((s) => s.setIsTestRun);
  const testRunLimit = useMatchingStore((s) => s.testRunLimit);
  const setTestRunLimit = useMatchingStore((s) => s.setTestRunLimit);
  const pipelineId = useMatchingStore((s) => s.pipelineId);
  const setPipelineId = useMatchingStore((s) => s.setPipelineId);

  // === local: dataset binding на ProcessControlBar ===
  // PCB требует `datasetId` в пропе (slot 1). matchingStore намеренно не хранит
  // datasetId (cross-project bleed risk) — local ephemeral.
  const [datasetId, setDatasetId] = useState<string | null>(null);

  // === local: chips modal ===
  const [pendingChip, setPendingChip] = useState<AggregateChipData | null>(null);
  const [eyeData, setEyeData] = useState<DisputedCardData | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);

  const downloadBlank = useDownloadBlankTemplate();
  const start = useStartMatching();
  const { data: pipeline } = usePipelineStatus(pipelineId);
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);

  // === Execution wiring ===
  const validManualProps = useMemo(
    () =>
      manualProperties
        .filter((p) => p.name.trim().length > 0)
        .map((p) => ({
          name: p.name.trim(),
          unit: p.unit,
          allowed_values: p.allowedValues,
          // §D.4 two manual modes
          mode: p.mode ?? 'only_mine',
          format_rule: p.mode === 'by_rule' ? p.formatRule : null,
        })),
    [manualProperties],
  );

  // M1a-FE §5.3: Matching language picker lives in MethodBlock (UA|RU for
  // marketplace / fixed label for user template / hidden for manual-only).
  // PCB slot 7 is the generic 10-language list used by Search/Generation —
  // we suppress it here (setOutputLanguage=undefined) so the matching page
  // has exactly one language picker, no duplicate chaos.

  const canStart =
    Boolean(projectId && datasetId && !start.isPending) &&
    (templateId !== null || validManualProps.length > 0);

  const handleStart = () => {
    if (!canStart || !datasetId) return;
    setStartError(null);
    start.mutate(
      {
        dataset_id: datasetId,
        tier_key: tierKey,
        module_keys: ['matching'],
        matching_template_id: templateId,
        matching_language: language,
        matching_stage: 'categories',
        is_test_run: isTestRun,
        // Full run ("All") ignores test_run_limit server-side, but the field is
        // still Pydantic-validated (le=20), so send an in-range value, not 50.
        // Fix 2026-08-02: M1a matching /pipeline/start 422.
        test_run_limit: isTestRun ? testRunLimit : 20,
        matching_manual_properties:
          validManualProps.length > 0 ? validManualProps : null,
        matching_input_columns:
          selectedInputColumns.length > 0 ? selectedInputColumns : null,
        matching_multivalue_delimiter: multivalueDelimiter,
      },
      {
        onSuccess: (data: PipelineRun) => setPipelineId(data.id),
        onError: (err) => {
          // P1-1 2026-08-03: читаем унифицированный backend-ответ {error:{message}} (main.py).
          setStartError(extractApiError(err, 'Не вдалося запустити матчинг'));
        },
      },
    );
  };

  const execution: ProcessExecutionState = {
    status: (pipeline?.status as ProcessExecutionState['status']) ?? 'idle',
    isStarting: start.isPending,
    canStart,
    startLabel: t('process.matching.start', { defaultValue: 'Run Matching' }),
    onStart: handleStart,
    errorMessage: startError,
  };

  // === Library apply/save — перенесены в единую workspace-библиотеку (P1.4) ===

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <Breadcrumbs />

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-primary-400" />
          {t('process.matching.title', { defaultValue: 'Matching' })}
        </h1>
      </header>

      <ProcessControlBar
        datasetId={datasetId}
        setDatasetId={setDatasetId}
        tierKey={tierKey}
        setTierKey={setTierKey}
        testRunLimit={testRunLimit}
        setTestRunLimit={setTestRunLimit}
        isTestRun={isTestRun}
        setIsTestRun={setIsTestRun}
        // M1a-FE §5.3: language picker lives in MethodBlock, NOT in PCB slot 7.
        // Passing undefined for both suppresses the generic 10-language slot.
        outputLanguage={undefined}
        setOutputLanguage={undefined}
        execution={execution}
        costEstimate={t('process.matching.costEstimate', {
          defaultValue: '0 cr',
        })}
      />

      <MethodBlock datasetId={datasetId} />
      <InputColumnChips datasetId={datasetId} />

      {/* §D.3: очередь новых свойств (dataset-scoped) */}
      <DiscoveredPropertiesQueue datasetId={datasetId} />

      {pipelineId && (
        <section className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400">
              {t('process.matching.aggregate.title', {
                defaultValue: 'Categories (aggregates)',
              })}
            </h2>
            {/* §C: «+ Добавить категорию» — минимальная форма → resolve-row */}
            <button
              type="button"
              onClick={() => setAddCategoryOpen(true)}
              disabled={!datasetId || !pipelineId}
              className="btn-secondary text-xs px-3 py-1 flex items-center gap-1.5"
              data-testid="add-category-button"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('process.matching.addCategory.button', {
                defaultValue: 'Add category',
              })}
            </button>
          </div>
          <AggregateChips
            pipelineId={pipelineId}
            onPendingRemove={setPendingChip}
          />
          <p className="text-xs text-surface-500">
            {t('process.matching.aggregate.hint', {
              defaultValue:
                'Click × on a chip to remove it and move affected rows to unresolved.',
            })}
          </p>
        </section>
      )}

      {pipelineId && (
        <DisputedCarousel
          pipelineId={pipelineId}
          datasetId={datasetId}
          onOpenEye={setEyeData}
        />
      )}

      {pipelineId && <UnresolvedSummary pipelineId={pipelineId} />}

      <ConfirmCategoriesButton />

      {/* M1b: after confirm token is set, show DeterminedPropertiesGrid.
          If the property run hasn't happened yet (empty meta) show a re-run
          CTA alongside. */}
      {pipelineId && useMatchingStore.getState().confirmToken && (
        <>
          <DeterminedPropertiesGrid pipelineId={pipelineId} />
          <RunPropertiesAgain pipelineId={pipelineId} />
          <ResultsTable pipelineId={pipelineId} />
        </>
      )}

      {/* === Help / format hint === */}
      <section className="card space-y-3 text-sm text-surface-300">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400 flex items-center gap-2">
          <Info className="w-4 h-4" />
          {t('process.matching.help.title', { defaultValue: 'Template format' })}
        </h2>
        <p>
          {t('process.matching.help.body', {
            defaultValue:
              'A template is an Excel file with one table of 12 columns. Rows = allowed values (enum) for properties in categories.',
          })}
        </p>
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => downloadBlank.mutate()}
            disabled={downloadBlank.isPending}
            className="btn-secondary flex items-center gap-2 text-xs"
          >
            <Download className="w-3.5 h-3.5" />
            {t('process.matching.help.downloadBlank', {
              defaultValue: 'Download blank template',
            })}
          </button>
          <Link
            to="/settings/system"
            className="text-xs text-primary-300 hover:text-primary-200 underline"
          >
            {t('process.matching.help.goToSettings', {
              defaultValue: 'Upload your template at /settings/system',
            })}
          </Link>
        </div>
      </section>

      {/* === Hidden audit row (dataset — это ephemeral binding к PCB; помогаем глазам) === */}
      <p className="text-[11px] text-surface-500 flex items-center gap-1.5">
        <Database className="w-3 h-3" />
        {datasetId
          ? t('process.matching.datasetBound', {
              defaultValue: 'Bound to dataset',
              id: datasetId.slice(0, 8),
            })
          : t('process.matching.datasetUnbound', {
              defaultValue: 'No dataset picked yet',
            })}
        {rowsResp && (
          <span className="ml-2 font-mono text-surface-500">
            rows: {rowsResp.rows?.length ?? 0}
          </span>
        )}
      </p>

      <RemoveConfirmModal
        chip={pendingChip}
        pipelineId={pipelineId}
        onClose={() => setPendingChip(null)}
      />
      <DisputedEyeModal
        data={eyeData}
        datasetId={datasetId}
        onClose={() => setEyeData(null)}
      />
      {addCategoryOpen && (
        <AddCategoryModal
          pipelineId={pipelineId}
          datasetId={datasetId}
          onClose={() => setAddCategoryOpen(false)}
        />
      )}
      <div className={cn('hidden')} aria-hidden />
    </div>
  );
}

// === M1b: Re-run property extraction ===

/**
 * RunPropertiesAgain — explicit button to re-trigger the property stage
 * after the user has toggled multivalue or edited manual cards. By default,
 * confirm-categories auto-enqueues the property run (TZ §2.4) — this is
 * just an escape hatch.
 */
function RunPropertiesAgain({ pipelineId }: { pipelineId: string }) {
  const { t } = useTranslation();
  const confirmToken = useMatchingStore((s) => s.confirmToken);
  const overrides = useMatchingStore((s) => s.propertyMultivalueOverrides);
  const delimiter = useMatchingStore((s) => s.propertyDelimiter);
  const startProperties = useStartProperties();

  function handleClick() {
    if (!confirmToken) return;
    startProperties.mutate({
      pipeline_id: pipelineId,
      confirm_token: confirmToken,
      multivalue_overrides: overrides,
      delimiter,
    });
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={startProperties.isPending || !confirmToken}
        className="btn-secondary text-xs flex items-center gap-2"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('process.matching.properties.rerun', {
          defaultValue: 'Re-run property extraction',
        })}
      </button>
    </div>
  );
}
